/**
 * User's recommended location IDs — shared via context so place modal, list cards,
 * and other surfaces stay in sync after recommend / unrecommend.
 */


import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useAuth } from '@/contexts';
import { recommendationService } from '@/services/recommendationService';
import { debug, error as logError } from '@/utils/logger';

export interface UseRecommendationCacheReturn {
  isRecommended: (locationId: string | undefined | null) => boolean;
  isLoading: boolean;
  isInitialized: boolean;
  refreshRecommendations: () => Promise<void>;
  addRecommendation: (locationId: string) => void;
  removeRecommendation: (locationId: string) => void;
  /** Client-side delta for GT Rec explicit-style counts (after successful recommend API). */
  explicitRecDeltaFor: (locationId: string | undefined | null) => number;
  bumpExplicitRecDelta: (locationId: string, delta: 1 | -1) => void;
  resetExplicitRecDeltas: () => void;
}

const RecommendationCacheContext = createContext<UseRecommendationCacheReturn | null>(null);

export function RecommendationCacheProvider({ children }: { children: React.ReactNode }) {
  const { user } = useAuth();
  const isAuthenticated = !!user;

  const [recommendedLocationIds, setRecommendedLocationIds] = useState<Set<string>>(new Set());
  /** Per-location offset applied to explicit GT Rec counts (list + combined modal). */
  const [explicitRecDeltaByLocationId, setExplicitRecDeltaByLocationId] = useState<Record<string, number>>(
    {},
  );
  const [isLoading, setIsLoading] = useState(false);
  const [isInitialized, setIsInitialized] = useState(false);

  const fetchRecommendations = useCallback(async () => {
    if (!isAuthenticated) {
      setRecommendedLocationIds(new Set());
      setIsInitialized(true);
      return;
    }

    setIsLoading(true);
    debug('[useRecommendationCache] Fetching recommendations from server');
    try {
      const locations = await recommendationService.getUserRecommendedLocations();
      const locationIds = new Set(
        locations
          .map((loc) => loc._id)
          .filter((id): id is string => typeof id === 'string' && id.length > 0),
      );
      setRecommendedLocationIds(locationIds);
      debug('[useRecommendationCache] Cache initialized', { count: locationIds.size });
    } catch (err) {
      logError('[useRecommendationCache] Error fetching recommendations', { error: err });
    } finally {
      setIsLoading(false);
      setIsInitialized(true);
    }
  }, [isAuthenticated]);

  const refreshRecommendations = useCallback(async () => {
    await fetchRecommendations();
  }, [fetchRecommendations]);

  const isRecommended = useCallback(
    (locationId: string | undefined | null): boolean => {
      if (!locationId) return false;
      return recommendedLocationIds.has(locationId);
    },
    [recommendedLocationIds],
  );

  const addRecommendation = useCallback((locationId: string) => {
    setRecommendedLocationIds((prev) => new Set([...prev, locationId]));
  }, []);

  const removeRecommendation = useCallback((locationId: string) => {
    setRecommendedLocationIds((prev) => {
      const next = new Set(prev);
      next.delete(locationId);
      return next;
    });
  }, []);

  const explicitRecDeltaFor = useCallback(
    (locationId: string | undefined | null): number => {
      if (!locationId) return 0;
      return explicitRecDeltaByLocationId[locationId] ?? 0;
    },
    [explicitRecDeltaByLocationId],
  );

  const bumpExplicitRecDelta = useCallback((locationId: string, delta: 1 | -1) => {
    setExplicitRecDeltaByLocationId((prev) => ({
      ...prev,
      [locationId]: (prev[locationId] ?? 0) + delta,
    }));
  }, []);

  const resetExplicitRecDeltas = useCallback(() => {
    setExplicitRecDeltaByLocationId({});
  }, []);

  useEffect(() => {
    if (isAuthenticated && !isInitialized) {
      void fetchRecommendations();
    }
  }, [isAuthenticated, isInitialized, fetchRecommendations]);

  useEffect(() => {
    if (!isAuthenticated && isInitialized) {
      setRecommendedLocationIds(new Set());
      setExplicitRecDeltaByLocationId({});
    }
  }, [isAuthenticated, isInitialized]);

  const value = useMemo(
    () => ({
      isRecommended,
      isLoading,
      isInitialized,
      refreshRecommendations,
      addRecommendation,
      removeRecommendation,
      explicitRecDeltaFor,
      bumpExplicitRecDelta,
      resetExplicitRecDeltas,
    }),
    [
      isRecommended,
      isLoading,
      isInitialized,
      refreshRecommendations,
      addRecommendation,
      removeRecommendation,
      explicitRecDeltaFor,
      bumpExplicitRecDelta,
      resetExplicitRecDeltas,
    ],
  );

  return (
    <RecommendationCacheContext.Provider value={value}>{children}</RecommendationCacheContext.Provider>
  );
}

export function useRecommendationCache(): UseRecommendationCacheReturn {
  const ctx = useContext(RecommendationCacheContext);
  if (!ctx) {
    throw new Error('useRecommendationCache must be used within RecommendationCacheProvider');
  }
  return ctx;
}
